import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import i18n from '@/i18n'
import { useGeneration, useUpdateGeneration } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { ASPECT_RATIO_GROUPS, type AspectRatio } from '@/lib/constants'

function isAspectRatio(value: string): value is AspectRatio {
  return ASPECT_RATIO_GROUPS.some((group) =>
    group.ratios.some((ratio) => ratio.id === value),
  )
}

// 어드민 UI는 한국어 고정 - 비율 라벨도 ko 리소스에서 직접 조회한다
const tKo = i18n.getFixedT('ko')

export function GenerationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: generation, isLoading } = useGeneration(id!)
  const updateMutation = useUpdateGeneration(id!)

  type GenerationFormData = {
    prompt: string
    modelId: string
    style: string
    aspectRatio: AspectRatio
  }

  const baseFormData = useMemo<GenerationFormData>(
    () =>
      generation
        ? {
            prompt: generation.prompt || '',
            modelId: generation.modelId || '',
            style: generation.style || '',
            aspectRatio: isAspectRatio(generation.aspectRatio) ? generation.aspectRatio : '1:1',
          }
        : {
            prompt: '',
            modelId: '',
            style: '',
            aspectRatio: '1:1',
          },
    [generation],
  )
  const [draftFormData, setDraftFormData] = useState<GenerationFormData | null>(null)
  const formData = draftFormData ?? baseFormData

  const updateField = <K extends keyof GenerationFormData>(
    key: K,
    value: GenerationFormData[K],
  ) => {
    setDraftFormData((prev) => ({ ...(prev ?? baseFormData), [key]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.prompt.trim()) {
      toast.error('프롬프트를 입력하세요')
      return
    }

    try {
      const updateData = {
        prompt: formData.prompt,
        modelId: formData.modelId,
        style: formData.style,
        aspectRatio: formData.aspectRatio,
      }

      await updateMutation.mutateAsync(updateData)
      toast.success('생성 이미지가 수정되었습니다')
      navigate(`/admin/generations/${id}/view`)
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '수정 실패')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-heading tracking-tight">생성 이미지 수정</h1>
        <p className="text-caption mt-2 leading-relaxed">생성된 이미지의 메타데이터를 수정합니다</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg border p-6">
        {/* Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            프롬프트 <span className="text-red-500">*</span>
          </label>
            <textarea
              value={formData.prompt}
              onChange={(e) => updateField('prompt', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              rows={4}
              required
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Model ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">모델 ID</label>
            <Input
              value={formData.modelId}
              onChange={(e) => updateField('modelId', e.target.value)}
              placeholder="google/gemini-3.1-flash-image"
            />
          </div>

          {/* Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">스타일</label>
            <Input
              value={formData.style}
              onChange={(e) => updateField('style', e.target.value)}
              placeholder="ppt, photo, sketch 등"
            />
          </div>

          {/* Aspect Ratio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">비율</label>
            <select
              value={formData.aspectRatio}
              onChange={(e) => {
                const nextRatio = e.target.value
                if (isAspectRatio(nextRatio)) {
                  updateField('aspectRatio', nextRatio)
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {ASPECT_RATIO_GROUPS.map((group) => (
                <optgroup key={group.labelKey} label={tKo(group.labelKey)}>
                  {group.ratios.map((ratio) => (
                    <option key={ratio.id} value={ratio.id}>
                      {ratio.id} - {tKo(ratio.labelKey)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3 pt-4">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '저장 중...' : '수정'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/admin/generations/${id}/view`)}
          >
            취소
          </Button>
        </div>
      </form>
    </div>
  )
}
